import React from "react";
import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { toast } from "sonner";
import { getShareLinks } from "@/utils/shareConfig";

const ShareModal = ({ open, onClose, url, title }) => {
  const shareMessage = `Take a look at this ${title} on Kidilan Traveler: `;
  const fullMessage = `${shareMessage}${url}`;

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: title,
          text: shareMessage,
          url: url,
        });
      } catch (err) {
        if (err.name !== "AbortError") {
          toast.error("Error sharing");
        }
      }
    } else {
      toast.error("Sharing not supported on this browser");
    }
  };

  const shareLinks = getShareLinks(url, title, handleNativeShare);

  const copyToClipboard = () => {
    navigator.clipboard.writeText(fullMessage);
    toast.success("Message & link copied!");
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{
        className: "rounded-[28px] overflow-hidden shadow-2xl",
        style: { borderRadius: '28px' }
      }}
    >
      <div className="p-6 bg-white relative">
        <div className="flex justify-between items-center mb-5">
          <div>
            <h2 className="text-xl font-display font-semibold text-slate-900 leading-tight">Share</h2>
            <p className="text-sm text-slate-500 font-medium truncate mt-0.5">{title}</p>
          </div>
          <IconButton 
            onClick={onClose} 
            size="small" 
            className="bg-slate-50 hover:bg-slate-100 text-slate-400 hover:text-slate-900 transition-all rounded-xl p-2"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>

        <div className="space-y-6">
          {/* Social Icons Section */}
          <div className="space-y-4">
            <p className="text-[10px] font-black uppercase tracking-[0.25em] text-slate-400">Share via Social</p>
            <div className="grid grid-cols-4 gap-4">
              {shareLinks.map((app) => (
                app.onClick ? (
                  <button
                    key={app.name}
                    onClick={app.onClick}
                    className="flex flex-col items-center gap-3 group"
                  >
                    <div className={`w-14 h-14 ${app.color} text-white rounded-full flex items-center justify-center shadow-lg shadow-slate-100 transition-all duration-300 group-hover:-translate-y-1 group-active:scale-95`}>
                      {React.cloneElement(app.icon, { fontSize: "medium" })}
                    </div>
                    <span className="text-[10px] font-bold text-slate-400 group-hover:text-slate-900 transition-colors uppercase tracking-widest">{app.name}</span>
                  </button>
                ) : (
                  <a
                    key={app.name}
                    href={app.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex flex-col items-center gap-3 group"
                  >
                    <div className={`w-14 h-14 ${app.color} text-white rounded-full flex items-center justify-center shadow-lg shadow-slate-100 transition-all duration-300 group-hover:-translate-y-1 group-active:scale-95`}>
                      {React.cloneElement(app.icon, { fontSize: "medium" })}
                    </div>
                    <span className="text-[10px] font-bold text-slate-400 group-hover:text-slate-900 transition-colors uppercase tracking-widest">{app.name}</span>
                  </a>
                )
              ))}
            </div>
          </div>

          {/* Copy Link Section */}
          <div className="space-y-4">
            <p className="text-[10px] font-black uppercase tracking-[0.25em] text-slate-400">Direct Link</p>
            <div className="flex items-center gap-3 p-2 pl-5 bg-slate-50 rounded-2xl border border-slate-100 focus-within:border-[#bfa27a] focus-within:ring-4 focus-within:ring-[#bfa27a]/5 transition-all">
              <div className="flex-1 min-w-0">
                <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider mb-0.5">Copy full message</p>
                <input
                  type="text"
                  value={url}
                  readOnly
                  className="bg-transparent border-none outline-none text-[#bfa27a] text-sm w-full truncate font-semibold hover:text-[#a88d66] cursor-pointer"
                  onClick={(e) => {
                    e.target.select();
                    copyToClipboard();
                  }}
                />
              </div>
              <button
                onClick={copyToClipboard}
                className="bg-[#bfa27a] text-white py-3 px-6 rounded-xl hover:bg-[#a88d66] transition-all shadow-xl shadow-[#bfa27a]/20 active:scale-95 flex items-center gap-2 group"
              >
                <ContentCopyIcon sx={{ fontSize: 16 }} className="group-hover:scale-110 transition-transform" />
                <span className="text-xs font-bold uppercase tracking-widest">Copy</span>
              </button>
            </div>
          </div>
        </div>

        <div className="mt-6 text-center opacity-20">
          <p className="text-[8px] text-slate-400 font-bold uppercase tracking-[0.4em]">Kidilan Traveler</p>
        </div>
      </div>
    </Dialog>
  );
};

export default ShareModal;
